import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import ScheduleList from './RuleList'
import { useScheduleSave } from '../hooks/useScheduleSave'
import { useScheduleDelete } from '../hooks/useScheduleDelete'
import { useScheduleActions } from '../hooks/useScheduleActions'
import { hasSchedulingEngineFeature } from 'utils/studyConfigUtils'

const SESSION_ITEM_TYPES = [
  INSTRUMENT_TYPE_MAP.survey,
  INSTRUMENT_TYPE_MAP.diary,
  INSTRUMENT_TYPE_MAP.clinro,
]

const typeLabel = type => {
  if (type === INSTRUMENT_TYPE_MAP.survey || type === INSTRUMENT_TYPE_MAP.diary) return 'ePRO'
  if (type === INSTRUMENT_TYPE_MAP.clinro) return 'ClinRO'
  if (type === INSTRUMENT_TYPE_MAP.article) return 'Training'
  return type
}

const ScheduleStepPage = ({
  entityOptions,
  study,
  studyID,
  instrument,
  dispatch,
  disabled,
  onPromptDeploy,
  studyVersionID,
  hasStudyPromotion,
}) => {
  const { saving } = useScheduleActions()
  const { onSave, onSaveAndAdd } = useScheduleSave({
    studyID,
    instrument,
    entityOptions,
    dispatch,
    hasSchedulingEngine: hasSchedulingEngineFeature(study),
  })
  const { removeSchedule } = useScheduleDelete({
    studyID,
    instrument,
    dispatch,
    studyVersionID,
    hasStudyPromotion,
  })
  const [deploying, setDeploying] = useState(false)

  const title = instrument?.metadata?.display_name || instrument?.title || 'Instrument'
  const showParticipantSession = SESSION_ITEM_TYPES.includes(instrument?.type)
  const busy = disabled || saving || deploying

  const saveAndAdd = async () => {
    await onSaveAndAdd()
  }

  const onDeploy = async () => {
    if (busy || !onPromptDeploy) return
    setDeploying(true)
    try {
      const ok = await onSave()
      if (ok) await onPromptDeploy(studyID)
    } finally {
      setDeploying(false)
    }
  }

  return (
    <div className='scheduling-engine page'>
      <header className='se-page-header'>
        <h1>Deployment Schedule</h1>
        <p className='se-subtitle'>
          {title}
          {instrument?.type ? ` · ${typeLabel(instrument.type)}` : ''}
        </p>
        <p className='se-wireframe-note'>
          Wireframe for Unified Scheduling field spec v0.3 — drawn in the current Study Builder styling, not the brand
          design system.
        </p>
      </header>
      <ScheduleList
        entityOptions={entityOptions}
        study={study}
        disabled={busy}
        showParticipantSession={showParticipantSession}
        instrumentName={title}
        onRemoveSchedule={removeSchedule}
      />
      <footer className='se-footer'>
        <div className='se-footer-actions'>
          <Button
            type='button'
            secondary
            no-margin
            className='se-save-add-button'
            content={saving ? 'Saving…' : 'Save & add another schedule'}
            disabled={busy}
            onClick={saveAndAdd}
          />
          <Button
            type='button'
            grey
            no-margin
            id='send'
            content={deploying ? 'Deploying…' : saving ? 'Saving…' : 'Deploy'}
            disabled={busy}
            onClick={onDeploy}
          />
        </div>
      </footer>
    </div>
  )
}

ScheduleStepPage.propTypes = {
  entityOptions: PropTypes.object,
  study: PropTypes.object,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  instrument: PropTypes.object,
  dispatch: PropTypes.func,
  disabled: PropTypes.bool,
  onPromptDeploy: PropTypes.func,
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasStudyPromotion: PropTypes.bool,
}

export default ScheduleStepPage
